/**
 * axios setup to use mock service
 */

import axios from 'axios';

// Configure baseURL differently for server (SSR) vs client
// - On the server: use the absolute API server to avoid relative URL issues in Node
// - On the client: keep relative paths to go through the Express proxy (/api -> API_SERVER)
//
// Configuration priority (server-side):
// 1. REACT_APP_API_SERVER: Direct API server URL (bypasses all proxies)
// 2. REACT_APP_NGINX_PROXY: Nginx proxy URL (for setups where nginx handles API proxying)
// 3. Fallback: localhost:PORT (goes through Express proxy in server.js)
const isDevelopment = process.env.NODE_ENV === 'development';
let serverBaseURL;

if (typeof window === 'undefined') {
    if (process.env.REACT_APP_API_SERVER) {
        serverBaseURL = process.env.REACT_APP_API_SERVER;
        // Ensure baseURL doesn't end with trailing slash
        if (serverBaseURL.endsWith('/')) {
            serverBaseURL = serverBaseURL.slice(0, -1);
        }
        if (isDevelopment) {
            console.log('[SSR] Axios: Using REACT_APP_API_SERVER:', serverBaseURL);
        }
    } else if (process.env.REACT_APP_NGINX_PROXY) {
        // If nginx handles API proxy, use nginx URL for SSR requests
        // This allows SSR server to make requests through nginx proxy
        serverBaseURL = process.env.REACT_APP_NGINX_PROXY;
        if (serverBaseURL.endsWith('/')) {
            serverBaseURL = serverBaseURL.slice(0, -1);
        }
        if (isDevelopment) {
            console.log('[SSR] Axios: Using REACT_APP_NGINX_PROXY (nginx proxy):', serverBaseURL);
        }
    } else {
        // If REACT_APP_API_SERVER is not set, use localhost with same port
        // This allows requests to go through the Express proxy (/api -> API_SERVER)
        // The Express proxy in server.js will forward /api requests to the actual API server
        // NOTE: This only works if the Express proxy is configured in server.js
        // IMPORTANT: If nginx handles API proxy instead of Express, you should either:
        // 1. Set REACT_APP_NGINX_PROXY to the nginx URL (e.g., http://localhost:80 or your domain)
        // 2. Set REACT_APP_API_SERVER to the actual API server URL (bypasses nginx for internal requests)
        const port = process.env.PORT || 3000;
        serverBaseURL = `http://localhost:${port}`;

        if (isDevelopment) {
            console.warn('[SSR] Axios: REACT_APP_API_SERVER not set, using localhost (Express proxy):', serverBaseURL);
            console.warn('[SSR] Axios: If nginx handles API proxy, set REACT_APP_NGINX_PROXY instead');
        }
    }
} else {
    // Client-side: use relative URLs to go through Express proxy
    serverBaseURL = '';
}

// CRITICAL: Ensure baseURL is always defined (even if empty string for client)
// This prevents axios from having undefined baseURL which causes issues
const axiosServices = axios.create({
    baseURL: serverBaseURL !== undefined ? serverBaseURL : '',
    // Add timeout for server-side requests to prevent hanging (30 seconds)
    timeout: typeof window === 'undefined' ? 30000 : undefined
});

// Request interceptor to validate baseURL on server
axiosServices.interceptors.request.use(
    (config) => {
        const isServer = typeof window === 'undefined';
        const isDevelopment = process.env.NODE_ENV === 'development';

        // On server, validate baseURL configuration
        if (isServer) {
            // If baseURL is empty/undefined and REACT_APP_API_SERVER is not set, this is a problem
            if (!config.baseURL && !process.env.REACT_APP_API_SERVER) {
                const port = process.env.PORT || 3000;
                // Set baseURL to localhost fallback if not already set
                config.baseURL = `http://localhost:${port}`;
                if (isDevelopment) {
                    console.warn('[SSR] Axios Request Interceptor: baseURL was empty, setting to localhost fallback:', config.baseURL);
                    console.warn('[SSR] Axios Request Interceptor: This means REACT_APP_API_SERVER is not set');
                    console.warn('[SSR] Axios Request Interceptor: Ensure Express proxy is configured in server.js');
                }
            }

            // Log request details for debugging (only in non-production to avoid log spam)
            if (isDevelopment && config.url) {
                const fullUrl = config.baseURL ? `${config.baseURL}${config.url}` : config.url;
                console.log('[SSR] Axios Request:', {
                    method: config.method || 'GET',
                    url: config.url,
                    baseURL: config.baseURL || '(empty - will use relative URL)',
                    fullUrl: fullUrl,
                    hasApiServer: !!process.env.REACT_APP_API_SERVER
                });
            }
        }

        return config;
    },
    (error) => Promise.reject(error)
);

// Response interceptor for http
axiosServices.interceptors.response.use(
    (response) => {
        // CRITICAL: Validate that API responses are JSON, not HTML
        // Some APIs return HTML error pages (404, 500, etc.) which would break the app
        const contentType = response.headers['content-type'] || '';
        const isJson = contentType.includes('application/json') || contentType.includes('text/json');
        const isString = typeof response.data === 'string';

        // If we got HTML instead of JSON, convert it to an error
        if (isString && response.data.trim().startsWith('<!')) {
            const isServer = typeof window === 'undefined';
            const requestUrl = response.config?.url || 'unknown';
            const requestBaseURL = response.config?.baseURL || '(empty)';
            const fullRequestUrl = requestBaseURL && requestBaseURL !== '(empty)' ? `${requestBaseURL}${requestUrl}` : requestUrl;

            const error = new Error('API returned HTML instead of JSON - likely an error page');
            error.response = {
                status: response.status || 500,
                statusText: response.statusText || 'Server Error',
                data: { message: 'API returned HTML instead of JSON' },
                headers: response.headers
            };
            error.config = response.config;
            error.isHtmlResponse = true;

            // Enhanced error logging in development (only when isDevelopment)
            if (isDevelopment) {
                console.error('[SSR] Axios: API returned HTML instead of JSON:', {
                    url: requestUrl,
                    baseURL: requestBaseURL,
                    fullRequestUrl: fullRequestUrl,
                    status: response.status,
                    contentType,
                    dataPreview: response.data.substring(0, 200),
                    isServer,
                    hasApiServerEnv: !!process.env.REACT_APP_API_SERVER,
                    apiServerEnv: process.env.REACT_APP_API_SERVER || 'NOT SET',
                    port: process.env.PORT || 'NOT SET',
                    nodeEnv: process.env.NODE_ENV || 'NOT SET'
                });

                // Additional diagnostic message
                if (isServer && (!requestBaseURL || requestBaseURL === '(empty)')) {
                    console.error('[SSR] Axios: DIAGNOSIS - baseURL is empty!');
                    console.error('[SSR] Axios: This means the request went to the SSR server instead of the API server');
                    console.error('[SSR] Axios: SOLUTION: Set REACT_APP_API_SERVER environment variable');
                    console.error('[SSR] Axios: OR ensure Express proxy is configured in server.js');
                }
            }

            return Promise.reject(error);
        }

        return response;
    },
    (error) => {
        // Handle API server not configured error
        if (error.code === 'API_SERVER_NOT_CONFIGURED') {
            return Promise.reject(error);
        }

        // Provide better error messages
        if (error.response) {
            // Server responded with error status
            const errorData = error.response.data || {
                message: error.response.statusText || `HTTP ${error.response.status}`,
                status: error.response.status,
                statusText: error.response.statusText
            };
            const axiosError = new Error(errorData.message || 'Request failed');
            Object.assign(axiosError, errorData);
            return Promise.reject(axiosError);
        }
        if (error.request) {
            // Request was made but no response received
            const isServer = typeof window === 'undefined';
            const apiServer = isServer ? process.env.REACT_APP_API_SERVER || 'NOT SET' : 'proxy';
            const baseURL = error.config?.baseURL || 'NOT SET';

            // Provide more helpful error message when baseURL is empty
            let errorMessage = `Network error: No response from server`;
            if (isServer && !baseURL && baseURL !== 'NOT SET') {
                errorMessage += ` (REACT_APP_API_SERVER is not configured)`;
            } else {
                errorMessage += isServer ? ` (${apiServer})` : '';
            }

            const networkError = new Error(errorMessage);
            networkError.code = error.code || 'NETWORK_ERROR';
            networkError.config = {
                url: error.config?.url,
                baseURL
            };
            return Promise.reject(networkError);
        }
        // Something else happened
        const configError = new Error(error.message || 'Request configuration error');
        configError.originalError = error;
        return Promise.reject(configError);
    }
);

export default axiosServices;
